// Focus and tap handling for the help component (components/help.html). The
// Popover API does the rest: opening, Escape, closing on outside clicks.
//
// Opening moves focus into the popover so screen readers read it straight
// away and Tab reaches its Close button. Closing returns focus to the
// question mark button if focus was still inside the popover. Browsers only
// do that themselves when the button had focus to begin with, which Safari
// doesn't give on click.

import { onElements } from './dom.js'

const initHelp = (popover) => {
    if (popover.dataset.helpInitialized) return
    popover.dataset.helpInitialized = 'true'

    // The question mark button, which comes before the popover's own Close button
    const trigger = popover.parentElement.querySelector('[popovertarget]')
    let focusWasInside = false

    // Checked before closing, as by `toggle` the hidden popover has lost focus
    popover.addEventListener('beforetoggle', (event) => {
        if (event.newState === 'closed') {
            focusWasInside = popover.contains(document.activeElement)
        }
    })

    popover.addEventListener('toggle', (event) => {
        if (event.newState === 'open') {
            popover.focus({ preventScroll: true })
        } else if (focusWasInside) {
            trigger.focus({ preventScroll: true })
        }
    })

    // Tabbing (or otherwise focusing) past it closes it, so it never covers
    // what the player has moved on to. Clicks on nothing focusable have no
    // relatedTarget and are left to the popover's own light dismiss.
    const wrapper = popover.parentElement
    wrapper.addEventListener('focusout', (event) => {
        const next = event.relatedTarget
        if (!next || wrapper.contains(next)) return
        if (!popover.matches(':popover-open')) return
        popover.hidePopover()
        // Focus has moved on, so don't pull it back to the trigger
        focusWasInside = false
    })
}

onElements('[data-help]', initHelp)

// A tap outside an open popover only closes it. Otherwise the click carries
// on to whatever was underneath, e.g. a modal's backdrop, closing the modal.
let dismissingTap = false

document.addEventListener(
    'pointerdown',
    (event) => {
        const open = document.querySelector('[data-help]:popover-open')
        dismissingTap =
            Boolean(open) && !open.parentElement.contains(event.target)
    },
    true
)

document.addEventListener(
    'click',
    (event) => {
        if (!dismissingTap) return
        dismissingTap = false
        event.preventDefault()
        event.stopPropagation()
    },
    true
)

// A scroll or keyboard press in between isn't followed by the tap's click
const resetDismissingTap = () => (dismissingTap = false)
document.addEventListener('pointercancel', resetDismissingTap, true)
document.addEventListener('keydown', resetDismissingTap, true)
